import {
	companyNameStyle,
	getStyleForColumn,
	reportInfoStyle,
	reportTitleStyle,
} from "./excelStyles.js";
import { deepMerge } from "../../../../../core/helpers/object-util.js";
import { fill } from "@foretagsplatsen/core-helpers/array";
import { makeEmptyCell, makeNumberCell, makeTextCell } from "./helper.js";
import CssClassNameVisitor from "../../../../../models/reports/reports/CssClassNameVisitor.js";
import ExcelColumnTitleRenderer from "./ExcelColumnTitleRenderer.js";
import ExcelRowRenderer from "./ExcelRowRenderer.js";
import ExcelRowTitleRenderer from "./ExcelRowTitleRenderer.js";
import ReportVisitor from "../../../../../models/reports/reports/ReportVisitor.js";

const defaultColumnWidth = 10;
const defaultFontSize = 11;

/**
 * Visit a Report, and output the data needed for xlsx-js-style to generate an
 * Excel file.
 *
 * @param {company} spec.company - Company the report is for.
 * @param {string} [spec.selectedDimensionsLabel] - String representing
 *   the selected dimensions. If defined, this string is shown in the
 *   document title.
 */
export default class ExcelExportingVisitor extends ReportVisitor {
	constructor({ company, selectedDimensionsLabel }) {
		super(...arguments);
		this._company = company;

		this._selectedDimensionsLabel = selectedDimensionsLabel
			? selectedDimensionsLabel.toString()
			: "";

		this._cssClassNameRenderer = new CssClassNameVisitor();

		// Usual visible columns (including separators) made for header and body
		this._reportVisibleColumns = undefined;

		// Excel specific columns for rendering body rows
		this._excelVisibleColumns = undefined;
	}

	visit(object) {
		return object.accept(this);
	}

	visitReport(report) {
		this._report = report;
		this._merges = [];
		this._rows = [];
		let data = [];

		this._reportVisibleColumns = report.getVisibleColumns();
		this._excelVisibleColumns = this._reportVisibleColumns;

		this._cols = fill({
			length: this._reportVisibleColumns.length + 1, // the plus one is because of the title column
			fn: () => ({ width: defaultColumnWidth }),
		});

		this._report.withCoALanguage(() => {
			data = [
				...this._tableHeadRenderer(),
				...this.visitAll(report.getVisibleRows()),
			];
		});

		return {
			data,
			merges: this._merges,
			rows: this._rows,
			cols: this._cols,
		};
	}

	visitComparisonReport(report) {
		this._report = report;
		this._merges = [];
		this._rows = [];
		let data = [];

		this._reportVisibleColumns = report.getVisibleColumns();
		this._excelVisibleColumns = report.getSerializableLeafColumns();

		this._report.withCoALanguage(() => {
			data = [
				...this._comparisonTableHeadRenderer(),
				...this.visitAll(report.getVisibleRows()),
			];
		});

		return {
			data,
			merges: this._merges,
			rows: this._rows,
			cols: this._cols,
		};
	}

	visitForecastReportReport(forecastReport) {
		this._report = forecastReport;
		this._merges = [];
		this._rows = [];
		let data = [];

		this._reportVisibleColumns = forecastReport.getVisibleColumns();
		this._excelVisibleColumns = this._reportVisibleColumns;

		this._cols = fill({
			length: this._reportVisibleColumns.length + 1,
			fn: () => ({ width: defaultColumnWidth }),
		});

		this._report.withCoALanguage(() => {
			data = [
				...this._tableHeadRenderer(),
				...this.visitAll(forecastReport.getVisibleRows()),
			];
		});

		return {
			data,
			merges: this._merges,
			rows: this._rows,
			cols: this._cols,
		};
	}

	visitAll(rows) {
		const result = [];

		rows.forEach((row) => {
			result.push(...this.visit(row));
		});

		return result;
	}

	visitAbstractRow(row) {
		return this._rowRenderer().visit(row);
	}

	_rowRenderer() {
		return new ExcelRowRenderer({
			report: this._report,
			columns: this._excelVisibleColumns,
			dataCellRenderer: this._dataCellRenderer.bind(this),
			rowTitleRenderer: this._rowTitleRenderer.bind(this),
			setRowLevel: this._setNextRowLevel.bind(this),
		});
	}

	_setNextRowLevel({ level }) {
		this._rows.push({ level });
	}

	_dataCellRenderer({ row, column, cssClass: style, content }) {
		const columnIndex = this._reportVisibleColumns.indexOf(column) + 1; // the plus one is because of the title column
		this._setColumnWidth({ columnIndex, content, style });

		style = deepMerge(style, getStyleForColumn({ column }));

		if (content === undefined) {
			return makeEmptyCell({ style });
		}

		return makeNumberCell({
			content,
			style,
			row,
			column,
		});
	}

	_rowTitleRenderer(row) {
		const rowTitleVisitor = new ExcelRowTitleRenderer({
			report: this._report,
			itemTitleRenderer: this._itemTitleRenderer.bind(this),
			budgetNameRenderer: this._budgetNameRenderer.bind(this),
			dimensionsRenderer: this._dimensionsRenderer.bind(this),
		});

		const cell = rowTitleVisitor.visit(row);

		this._setColumnWidth({
			columnIndex: 0,
			content: cell.v,
			style: cell.s,
		});

		return cell;
	}

	_itemTitleRenderer(item) {
		return item.getFullTitleString();
	}

	_budgetNameRenderer(budgetName) {
		return budgetName;
	}

	_dimensionsRenderer(item) {
		return item.hasOwnDimensionSpecification()
			? item.getDimensionLabels().join(" ")
			: "";
	}

	/**
	 * Calculates the number of subcolumns at the closest leaf
	 * depth. It returns the minimal number to render the subcolumns
	 * without adding empty children to leaves.
	 *
	 * To get the proper number, we would need to make cell models and
	 * dispatch this method among them.
	 */
	_getMinimumNumberOfLeaves(row) {
		if (!row.children) return 1;

		const childrenNumbers = row.children.map((childRow) =>
			this._getMinimumNumberOfLeaves(childRow),
		);

		if (childrenNumbers.some((number) => number === 1))
			return row.children.length;

		return Math.max(...childrenNumbers);
	}

	/**
	 * Calculates the number of rows that will be needed to render all
	 * the header subcolumns.
	 */
	_computeHeaderHeight(row) {
		if (!row.children) return 1;

		const childrenHeight = Math.max(
			row.children.map((childRow) => this._computeHeaderHeight(childRow)),
		);

		return 1 + childrenHeight;
	}

	_tableHeadRenderer() {
		const reportTitleRows = this._reportTitleRenderer();
		const numberOfTitleRows = reportTitleRows.length;

		const headerRows = [
			...reportTitleRows,
			this._companyNameRenderer({ numberOfTitleRows }),
		];

		this._mergeRow({ rowIndex: numberOfTitleRows + 1 });

		headerRows.push(
			...this._selectedDimensionRenderer({ numberOfTitleRows }),
			[],
		);

		headerRows.forEach((row) => {
			this._rows.push({ level: 0 });

			row.forEach((cell, columnIndex) => {
				this._setColumnWidth({
					columnIndex,
					content: cell.v,
					style: cell.s,
				});
			});
		});

		return headerRows;
	}

	_comparisonTableHeadRenderer() {
		const reportTitleRows = this._comparisonReportTitleRenderer();

		this._cols = this._makeHeaderColumns({ reportTitleRows });
		const headerRows = this._makeHeaderRows({ reportTitleRows });

		headerRows.forEach((row) => {
			this._rows.push({ level: 0 });

			row.forEach(({ v, s }, columnIndex) => {
				this._setColumnWidth({ columnIndex, content: v, style: s });
			});
		});

		this._rows.push({ level: 0 });

		return headerRows;
	}

	_makeHeaderColumns({ reportTitleRows }) {
		const numberOfColumns = reportTitleRows.reduce(
			(sum, reportTitleRow) =>
				sum + this._getMinimumNumberOfLeaves(reportTitleRow),
			0,
		);

		return fill({
			length: numberOfColumns,
			fn: () => ({ width: defaultColumnWidth }),
		});
	}

	_makeHeaderRows({ reportTitleRows }) {
		const numberOfTitleRows = reportTitleRows.reduce(
			(sum, reportTitleRow) =>
				sum + this._computeHeaderHeight(reportTitleRow),
			0,
		);

		const headerRows = [
			...this._makeTitleRows({ reportTitleRows }),
			this._companyNameRenderer({ numberOfTitleRows }),
			...this._selectedDimensionRenderer({ numberOfTitleRows }),
		];

		this._mergeColumn({
			rowIndex: numberOfTitleRows + 1,
			startColumnIndex: 0,
			endColumnIndex: this._reportVisibleColumns.length,
		});

		return headerRows;
	}

	_makeTitleRows({ reportTitleRows, rowIndex = 0 }) {
		const titles = reportTitleRows.map(
			(reportTitleRow) => reportTitleRow.titleCell || reportTitleRow,
		);

		const childrenTitles = this._makeChildrenTitleRows({
			reportTitleRows,
			titles,
			rowIndex,
		});

		if (!childrenTitles.length) return titles;

		const marginCells = fill({ length: rowIndex + 1, fn: makeEmptyCell });

		return [titles, [...marginCells, ...childrenTitles]];
	}

	_makeChildrenTitleRows({ reportTitleRows, titles, rowIndex }) {
		let columnIndex = 0;
		const childrenTitles = [];

		reportTitleRows.forEach(({ children }) => {
			if (!children) {
				columnIndex++;
				return;
			}

			childrenTitles.push(
				...this._makeTitleRows({
					reportTitleRows: children,
					rowIndex: rowIndex + 1,
				}),
			);

			this._widenTitles({
				titles,
				rowIndex,
				columnIndex,
				length: children.length - 1,
			});

			columnIndex += children.length;
		});

		return childrenTitles;
	}

	_widenTitles({ titles, rowIndex, columnIndex, length }) {
		const extraColumns = fill({ length, fn: makeEmptyCell });
		titles.splice(columnIndex + 1, 0, ...extraColumns);

		this._mergeColumn({
			rowIndex,
			startColumnIndex: columnIndex,
			endColumnIndex: columnIndex + length,
		});
	}

	_reportTitleRenderer() {
		const columnsTitleVisitor = this._makeColumnTitleVisitor({
			itemTitleRenderer: (column) => column.getFullTitleString(),
		});
		const columnTitlesRow = [];
		const propertiesRow = [];

		this._reportVisibleColumns.forEach((column) => {
			const { titleCell, propertiesCell } =
				columnsTitleVisitor.visit(column);
			columnTitlesRow.push(titleCell);
			if (!propertiesCell) return;
			propertiesRow.push(propertiesCell);
		});

		const firstRow = [
			makeTextCell({
				content: this._report.getTitle().toUpperCase(),
				style: reportTitleStyle,
			}),
			...columnTitlesRow,
		];

		return this._isEmptyRow(propertiesRow)
			? [firstRow]
			: [firstRow, [makeEmptyCell(), ...propertiesRow]];
	}

	_makeColumnTitleVisitor({ itemTitleRenderer }) {
		return new ExcelColumnTitleRenderer({
			itemTitleRenderer,
			dimensionsRenderer: this._dimensionsRenderer.bind(this),
			budgetNameRenderer: this._budgetNameRenderer.bind(this),
		});
	}

	_comparisonReportTitleRenderer() {
		const columnsTitleVisitor = this._makeColumnTitleVisitor({
			itemTitleRenderer: (column) => column.getTitle(),
		});

		const columnTitlesRow = [];
		const propertiesRow = [];

		const titles = this._reportVisibleColumns.flatMap((column) => {
			const columnTitle = columnsTitleVisitor.visit(column);
			return columnTitle.titleCell ? columnTitle : columnTitle.children;
		});

		titles.forEach(({ titleCell, propertiesCell, children }) => {
			columnTitlesRow.push({ titleCell, children });
			if (propertiesCell) propertiesRow.push(propertiesCell);
		});

		const firstRow = [
			makeTextCell({
				content: this._report.getTitle().toUpperCase(),
				style: reportTitleStyle,
			}),
			...columnTitlesRow,
		];

		return this._isEmptyRow(propertiesRow)
			? [...firstRow]
			: [...firstRow, [makeEmptyCell(), ...propertiesRow]];
	}

	_setColumnWidth({ columnIndex, content, style }) {
		const factor = (style?.font?.sz ?? defaultFontSize) / defaultFontSize;
		const cellLength = (content?.toString().length + 1) * factor;

		if (this._cols[columnIndex].width < cellLength) {
			this._cols[columnIndex] = { width: cellLength };
		}
	}

	_companyNameRenderer({ numberOfTitleRows }) {
		this._mergeRow({ rowIndex: numberOfTitleRows });

		return [
			makeTextCell({
				content: this._company.getName().toUpperCase(),
				style: companyNameStyle,
			}),
		];
	}

	_selectedDimensionRenderer({ numberOfTitleRows }) {
		if (!this._selectedDimensionsLabel) {
			return [];
		}

		this._mergeRow({ rowIndex: numberOfTitleRows + 2 });

		return [
			[
				makeTextCell({
					content: this._selectedDimensionsLabel,
					style: reportInfoStyle,
				}),
			],
		];
	}

	_mergeRow({ rowIndex, colspan = this._reportVisibleColumns.length }) {
		this._mergeColumn({
			rowIndex,
			startColumnIndex: 0,
			endColumnIndex: colspan,
		});
	}

	_mergeColumn({ rowIndex, startColumnIndex, endColumnIndex }) {
		this._merges.push({
			s: { r: rowIndex, c: startColumnIndex },
			e: { r: rowIndex, c: endColumnIndex },
		});
	}

	_isEmptyRow(row) {
		return row.every((cell) => cell.isEmpty);
	}
}
